// NOTE : UserDoc corresponds to User entity

import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import dayjs, { Dayjs } from 'dayjs';
import { UserRole } from '@leedpulse-monorepo/enums';
import UserEmails from './user_doc/UserEmails';
import { TUserCompanies, TUserSubscriptions } from '@leedpulse-monorepo/types';
import { IUser } from '@leedpulse-monorepo/interfaces';

// But because it clashes with the User Firebase type, it is named UserDoc
export default class UserDoc {
  private _id: string;
  private _uid: string;
  private _avatar: string;
  private _email: string; // send(create)
  private _emailAuthentication: string; // send(create)
  private _emails: UserEmails;
  private _firstname: string; // send(create)
  private _idCompanies: TUserCompanies;
  private _idSubscription: TUserSubscriptions;
  private _lastname: string; // send(create)
  private _phone: string; // send(create)
  private _role: UserRole; // send(create)
  private _daysOffStart: Dayjs | null;
  private _daysOffEnd: Dayjs | null;
  private _created: Date;
  private _updated: Date;
  private _last_login: Date;
  private _partnerUid: string;
  private _partnerSystem: string;
  private _position: string;
  private _apiKey: string;
  private _apiKeyEnabled: boolean;
  private _apiKeyGeneratedAt: number;
  private _passwordChangeRequiredAt: number | null;

  constructor(
    id: string,
    uid: string,
    avatar: string,
    email: string,
    emailAuthentication: string,
    emails: UserEmails,
    firstname: string,
    idCompanies: TUserCompanies,
    idSubscription: TUserSubscriptions,
    lastname: string,
    phone: string,
    role: UserRole,
    daysOffStart: Dayjs | null = null,
    daysOffEnd: Dayjs | null = null,
    created: Date,
    updated: Date,
    last_login: Date,
    partner_uid: string,
    partner_system: string,
    position: string,
    api_key = '',
    api_key_enabled = false,
    api_key_generated_at = 0,
    password_change_required_at: number | null = null
  ) {
    this._id = id;
    this._uid = uid;
    this._avatar = avatar;
    this._email = email;
    this._emailAuthentication = emailAuthentication;
    this._emails = emails;
    this._firstname = firstname;
    this._idCompanies = idCompanies;
    this._idSubscription = idSubscription;
    this._lastname = lastname;
    this._phone = phone;
    this._role = role;
    this._daysOffStart = daysOffStart;
    this._daysOffEnd = daysOffEnd;
    this._created = created;
    this._updated = updated;
    this._last_login = last_login;
    this._partnerUid = partner_uid;
    this._partnerSystem = partner_system;
    this._position = position;
    this._apiKey = api_key;
    this._apiKeyEnabled = api_key_enabled;
    this._apiKeyGeneratedAt = api_key_generated_at;
    this._passwordChangeRequiredAt = password_change_required_at;
  }

  get id(): string {
    return this._id;
  }

  get uid(): string {
    return this._uid;
  }

  get avatar(): string {
    return this._avatar;
  }

  get email(): string {
    return this._email;
  }

  get emailAuthentication(): string {
    return this._emailAuthentication;
  }

  get emails(): UserEmails {
    return this._emails;
  }

  get firstname(): string {
    return this._firstname;
  }

  get idCompanies(): TUserCompanies {
    return this._idCompanies;
  }

  get idSubscription(): TUserSubscriptions {
    return this._idSubscription;
  }

  get lastname(): string {
    return this._lastname;
  }

  get phone(): string {
    return this._phone;
  }

  get role(): UserRole {
    return this._role;
  }

  get daysOffStart(): Dayjs | null {
    return this._daysOffStart;
  }

  get daysOffEnd(): Dayjs | null {
    return this._daysOffEnd;
  }

  get daysOffAsTimestamps(): [number | null, number | null] {
    return [this._daysOffStart?.valueOf() || null, this._daysOffEnd?.valueOf() || null];
  }

  get created(): Date {
    return this._created;
  }

  get updated(): Date {
    return this._updated;
  }

  get last_login(): Date {
    return this._last_login;
  }

  get partnerUid(): string {
    return this._partnerUid;
  }

  get partnerSystem(): string {
    return this._partnerSystem;
  }

  get position(): string {
    return this._position;
  }

  get apiKey(): string {
    return this._apiKey;
  }

  get apiKeyEnabled(): boolean {
    return this._apiKeyEnabled;
  }

  get apiKeyGeneratedAt(): number {
    return this._apiKeyGeneratedAt;
  }

  get passwordChangeRequiredAt(): number | null {
    return this._passwordChangeRequiredAt;
  }

  public copyWith({
    avatar,
    emails,
    firstname,
    lastname,
    idCompanies,
    idSubscription,
    phone,
    role,
    daysOffStart,
    daysOffEnd,
    created,
    updated,
    last_login,
    partner_uid,
    partner_system,
    position,
    api_key,
    api_key_enabled,
    api_key_generated_at,
  }: {
    avatar?: string;
    emails?: UserEmails;
    firstname?: string;
    lastname?: string;
    idCompanies?: TUserCompanies;
    idSubscription?: TUserSubscriptions;
    phone?: string;
    role?: UserRole;
    daysOffStart?: Dayjs | null;
    daysOffEnd?: Dayjs | null;
    created?: Date;
    updated?: Date;
    last_login?: Date;
    partner_uid?: string;
    partner_system?: string;
    position?: string;
    api_key?: string;
    api_key_enabled?: boolean;
    api_key_generated_at?: number;
  }): UserDoc {
    return new UserDoc(
      this._id,
      this._uid,
      avatar ?? this._avatar,
      this._email,
      this._emailAuthentication,
      emails ?? this._emails,
      firstname ?? this._firstname,
      idCompanies ?? this._idCompanies,
      idSubscription ?? this._idSubscription,
      lastname ?? this._lastname,
      phone ?? this._phone,
      role ?? this._role,
      daysOffStart ?? this._daysOffStart,
      daysOffEnd ?? this._daysOffEnd,
      created ?? this._created,
      updated ?? this._updated,
      last_login ?? this._last_login,
      partner_uid ?? this._partnerUid,
      partner_system ?? this._partnerSystem,
      position ?? this._position,
      api_key ?? this._apiKey,
      api_key_enabled ?? this._apiKeyEnabled,
      api_key_generated_at ?? this._apiKeyGeneratedAt,
      this._passwordChangeRequiredAt
    );
  }

  static fromFirestore(doc: QueryDocumentSnapshot<DocumentData, DocumentData>): UserDoc {
    const docData = doc.data();
    return new UserDoc(
      docData.id,
      docData.uid,
      docData.avatar,
      docData.email,
      docData.email_authentication,
      UserEmails.fromFirestoreMap(docData.emails),
      docData.firstname,
      docData.id_companies,
      docData.id_subscription,
      docData.lastname,
      docData.phone,
      docData.role,
      docData.days_off?.[0] ? dayjs(docData.days_off[0]) : null,
      docData.days_off?.[1] ? dayjs(docData.days_off[1]) : null,
      docData.created,
      docData.updated,
      docData.last_login,
      docData.partner_uid || '',
      docData.partner_system || '',
      docData.position || '',
      docData.api_key || '',
      docData.api_key_enabled ?? true,
      docData.api_key_generated_at || 0,
      docData.password_change_required_at ?? null
    );
  }

  static fromJson(json: IUser): UserDoc {
    return new UserDoc(
      json.id,
      json.uid,
      json.avatar,
      json.email,
      json.email_authentication,
      UserEmails.fromFirestoreMap(json.emails),
      json.firstname,
      json.id_companies,
      json.id_subscription,
      json.lastname,
      json.phone,
      json.role,
      json.days_off?.[0] ? dayjs(json.days_off[0]) : null,
      json.days_off?.[1] ? dayjs(json.days_off[1]) : null,
      dayjs(json.created).toDate(),
      dayjs(json.updated).toDate(),
      dayjs(json.last_login).toDate(),
      json.partner_uid || '',
      json.partner_system || '',
      json.position || '',
      json.api_key || '',
      json.api_key_enabled ?? true,
      json.api_key_generated_at || 0,
      json.password_change_required_at ?? null
    );
  }

  toPatch(): Record<string, any> {
    return {
      id: this._id,
      uid: this._uid,
      avatar: this._avatar,
      emails: this._emails.toPatch(),
      firstname: this._firstname,
      lastname: this._lastname,
      id_subscription: this._idSubscription,
      phone: this._phone,
      role: this._role,
      days_off: this.daysOffAsTimestamps,
      updated: new Date().getTime(),
      partner_uid: this._partnerUid,
      partner_system: this._partnerSystem,
      position: this._position,
    };
  }
}
